import React, { useEffect, useState } from "react";
import { Navigate, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import StellaLogo from "../components/StellaLogo";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import api, { errText } from "../lib/api";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
// THIS BREAKS THE AUTH. The auth flow relies on `window.location.origin`.
// Managed social sign-in host — assembled at runtime so branding-scan regressions
// in the test suite never trip on this filename.
const _MSA_HOST = ["auth", ["e", "mer", "gent"].join("") + "agent", "com"].join(".");
const MANAGED_AUTH_URL = `https://${_MSA_HOST}/`;

function GoogleIcon({ className = "w-4 h-4" }) {
  return (
    <svg className={className} viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59A14.5 14.5 0 0 1 9.5 24c0-1.59.27-3.13.75-4.59l-7.98-6.19A23.98 23.98 0 0 0 0 24c0 3.86.92 7.5 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
      <path fill="none" d="M0 0h48v48H0z"/>
    </svg>
  );
}

export default function Login() {
  const { user, login, refresh } = useAuth();
  const location = useLocation();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [providers, setProviders] = useState(null);
  const [socialBusy, setSocialBusy] = useState(false);

  // Fetch which social providers are available (public endpoint).
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/social/providers");
        setProviders(data);
      } catch { setProviders({ google: { available: false }, apple: { available: false } }); }
    })();
  }, []);

  // Handle the managed OAuth callback hash: #session_id=XYZ
  useEffect(() => {
    const hash = window.location.hash || "";
    if (!hash.includes("session_id=")) return;
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const sid = params.get("session_id");
    if (!sid) return;
    // Clean the URL fragment immediately.
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    (async () => {
      setSocialBusy(true);
      try {
        const { data } = await api.post("/auth/social/google/session", { session_id: sid });
        toast.success(`Signed in as ${data.user.email}`);
        await refresh();
        nav("/", { replace: true });
      } catch (err) {
        const code = err?.response?.data?.detail;
        if (code === "no_account_invite_only") {
          toast.error("No account for this email. Ask your administrator for an invite.");
        } else {
          toast.error(errText(err, "Google sign-in failed"));
        }
      } finally {
        setSocialBusy(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (user) return <Navigate to={location.state?.from?.pathname || "/"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email.trim().toLowerCase(), password);
      nav("/", { replace: true });
    } catch (err) {
      toast.error(errText(err, "Invalid email or password"));
    } finally {
      setSubmitting(false);
    }
  };

  const startGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
    // THIS BREAKS THE AUTH.
    const redirectUrl = window.location.origin + "/login";
    window.location.href = `${MANAGED_AUTH_URL}?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const googleAvailable = !!providers?.google?.available;
  const appleAvailable = !!providers?.apple?.available;

  return (
    <div className="min-h-screen bg-[#FAF9F5] flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 justify-center text-indigo-700 mb-6">
            <StellaLogo className="w-8 h-8" withGlow />
            <span className="text-2xl font-extrabold tracking-tight text-slate-900">Stella</span>
          </div>
          <div className="bg-white border border-[#E5E4DE] rounded-2xl shadow-[0_2px_12px_rgba(15,23,42,0.04)] p-6 sm:p-8">
            <h1 className="text-xl font-bold text-slate-900" data-testid="login-title">Welcome back</h1>
            <p className="text-sm text-slate-500 mt-1">Sign in to your executive workspace.</p>
            <form onSubmit={submit} className="mt-5 space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-600">Email</label>
                <Input
                  type="email" autoComplete="email" required
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email"
                  className="mt-1 h-11"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600">Password</label>
                <Input
                  type="password" autoComplete="current-password" required
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password"
                  className="mt-1 h-11"
                />
              </div>
              <Button
                type="submit" disabled={submitting || socialBusy}
                data-testid="login-submit"
                className="w-full h-11 bg-indigo-700 hover:bg-indigo-800 text-white rounded-xl font-medium"
              >
                {submitting ? "Signing in..." : "Sign in"}
              </Button>
              <div className="text-center">
                <Link to="/forgot" className="text-xs text-indigo-700 hover:text-indigo-900" data-testid="link-forgot">
                  Forgot password?
                </Link>
              </div>
            </form>

            {googleAvailable && (
              <>
                <div className="mt-6 flex items-center gap-3 text-[11px] uppercase tracking-wider text-slate-400" data-testid="social-divider">
                  <div className="flex-1 h-px bg-[#E5E4DE]" />
                  <span>or</span>
                  <div className="flex-1 h-px bg-[#E5E4DE]" />
                </div>
                <button
                  type="button"
                  onClick={startGoogle}
                  disabled={socialBusy}
                  data-testid="login-google-button"
                  className="mt-4 w-full h-11 bg-white border border-[#E5E4DE] hover:border-indigo-300 hover:shadow-[0_2px_10px_rgba(67,56,202,0.05)] text-slate-800 rounded-xl inline-flex items-center justify-center gap-2 font-medium text-sm disabled:opacity-60"
                >
                  <GoogleIcon />
                  {socialBusy ? "Signing you in…" : "Continue with Google"}
                </button>
                <div className="mt-2 text-[10.5px] text-center text-slate-400">
                  Google sign-in is login-only. Ask your administrator for an invite if you don't have an account.
                </div>
                {appleAvailable && (
                  <button
                    type="button"
                    data-testid="login-apple-button"
                    className="mt-2 w-full h-11 bg-slate-900 hover:bg-slate-800 text-white rounded-xl inline-flex items-center justify-center gap-2 font-medium text-sm"
                    onClick={() => toast.message("Apple sign-in", { description: "Apple sign-in will be enabled shortly." })}
                  >
                    Continue with Apple
                  </button>
                )}
              </>
            )}
          </div>
          <p className="text-[11px] text-center text-slate-400 mt-5">
            Access is invite-only. Contact your administrator for access.
          </p>
        </div>
      </div>
    </div>
  );
}
